import { datadogLogs } from '@datadog/browser-logs';
import { Integration_Auth_Names_Enum, Integration_Names_Enum } from '@main/graphql/types.generated';
import { toError } from '@main/shared/utils';
import { errorToast, useAlertDialog } from '@main/ui';
import { useTranslation } from 'react-i18next';

import {
  useDeleteOrgIntegrationMutation,
  useGetAllOrgIntegrationDetailsQuery,
  useInsertOrgIntegrationBackofficeMutation,
  useLazyGetIntegrationIdByNameQuery,
  useLazyGetOrgIntegrationConfigStateBackofficeQuery,
  useSetOrgIntegrationConfigMutation,
  useUpdateOrgIntegrationBackofficeMutation,
} from './org-integrations.admin.generated';

export function useOrgIntegrations({ orgId }: { orgId: string }) {
  const { t } = useTranslation('integrations');
  const { openDialog } = useAlertDialog();
  const { data, refetch } = useGetAllOrgIntegrationDetailsQuery({ orgId });
  const orgIntegrations = data?.organization_integrations;

  const [getIntegrationIdByName] = useLazyGetIntegrationIdByNameQuery();
  const [_getOrgIntegrationConfigState] = useLazyGetOrgIntegrationConfigStateBackofficeQuery();
  const [_insertOrgIntegration] = useInsertOrgIntegrationBackofficeMutation();
  const [updateOrgIntegration] = useUpdateOrgIntegrationBackofficeMutation();
  const [_setOrgIntegrationConfig] = useSetOrgIntegrationConfigMutation();
  const [_deleteOrgIntegration] = useDeleteOrgIntegrationMutation();

  const insertOrgIntegration = async (
    providerName: Integration_Names_Enum,
    connectorName: string,
    authName: Integration_Auth_Names_Enum,
  ) => {
    try {
      const { integrations } = await getIntegrationIdByName({ providerName }).unwrap();
      const integrationId = integrations[0]?.id;
      if (!integrationId) {
        throw new Error(`Could not find integration id by name: ${providerName}`);
      }
      await _insertOrgIntegration({
        orgId,
        integrationId,
        connectorName,
        authName,
      }).unwrap();
      await refetch().unwrap();
    } catch (error) {
      const message = 'Failed creating integration';
      errorToast(message);
      datadogLogs.logger.error(message, { orgId, providerName }, toError(error));
    }
  };

  const setOrgIntegrationDisabled = async (orgIntegrationId: string, disabled: boolean) => {
    try {
      await updateOrgIntegration({
        orgIntegrationId,
        orgIntegration: {
          disabled,
        },
      }).unwrap();
      await refetch().unwrap();
    } catch (error) {
      const message = `Failed ${disabled ? 'disabling' : 'enabling'} integration`;
      errorToast(message);
      datadogLogs.logger.error(message, { orgId, orgIntegrationId }, toError(error));
    }
  };

  const setOrgIntegrationConfigurableByUser = async (
    orgIntegrationId: string,
    isConfigurableByUser: boolean,
  ) => {
    try {
      await updateOrgIntegration({
        orgIntegrationId,
        orgIntegration: {
          is_configurable_by_user: isConfigurableByUser,
        },
      }).unwrap();
      await refetch().unwrap();
    } catch (error) {
      const message = 'Failed updating integration';
      errorToast(message);
      datadogLogs.logger.error(message, { orgId, orgIntegrationId }, toError(error));
    }
  };

  const setOrgIntegrationConfig = async (
    orgIntegrationId: string,
    input: {
      config: Record<string, unknown>;
      encryptedConfigPatch: Record<string, unknown>;
    },
  ) => {
    try {
      const state = await _setOrgIntegrationConfig({
        input: {
          orgIntegrationId,
          config: input.config,
          encryptedConfigPatch: input.encryptedConfigPatch,
        },
      }).unwrap();
      if (!state.set_org_integration_config) {
        throw new Error(`Could not save integration config state: ${orgIntegrationId}`);
      }
      await refetch().unwrap();
      return state.set_org_integration_config;
    } catch (error) {
      const message = 'Failed updating integration config';
      errorToast(message);
      datadogLogs.logger.error(message, { orgId, orgIntegrationId }, toError(error));
      throw error;
    }
  };

  const deleteOrgIntegration = (orgIntegrationId: string) => {
    const orgIntegration = orgIntegrations?.find(({ id }) => id === orgIntegrationId);
    if (!orgIntegration) return;
    const providerName = t(`integrations.${orgIntegration.integration.name}.name`);

    return openDialog({
      dialogHeader: t('deleteConnectorDialog.header'),
      dialogContent: t('deleteConnectorDialog.content', { providerName }),
      confirmAction: {
        children: t('deleteConnectorDialog.confirmBtn'),
        onClick: async () => {
          try {
            await _deleteOrgIntegration({ orgIntegrationId }).unwrap();
            await refetch().unwrap();
          } catch (error) {
            const message = t('deleteConnectorDialog.errorMsg');
            errorToast(message);
            datadogLogs.logger.error(message, { orgId, orgIntegrationId }, toError(error));
          }
        },
      },
    });
  };

  const getOrgIntegrationConfigState = async (orgIntegrationId: string) => {
    const state = await _getOrgIntegrationConfigState({ orgIntegrationId }).unwrap();
    if (!state.org_integration_config_state) {
      throw new Error(`Could not load integration config state: ${orgIntegrationId}`);
    }
    return state.org_integration_config_state;
  };

  return {
    orgIntegrations,
    insertOrgIntegration,
    setOrgIntegrationDisabled,
    setOrgIntegrationConfigurableByUser,
    setOrgIntegrationConfig,
    getOrgIntegrationConfigState,
    deleteOrgIntegration,
  };
}
